import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    addRoutes,
    clearRoutes,
    setLoading as setReduxLoading,
    setHeaderLoading,
    setError as setReduxError,
    selectAllRoutes,
    selectTravelLoading,
    selectHeaderLoading,
    selectTravelError
} from '../store/slices/travelSlice';

//const WEBSOCKET_URL = "wss://aluy1ihmkd.execute-api.ap-south-1.amazonaws.com/development?api_key=web_dev_1";
const WEBSOCKET_URL = "wss://h5nkqou4f7.execute-api.ap-south-1.amazonaws.com/production";
const RECONNECT_INTERVAL = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;

export const useTravelWebSocket = () => {
    const dispatch = useDispatch();
    const [connectionStatus, setConnectionStatus] = useState('disconnected'); // connecting, open, closed, error
    const [messages, setMessages] = useState([]);
    const [searchMetadata, setSearchMetadata] = useState({
        user_src_ll: null,
        user_dest_ll: null,
        user_src_name: "",
        user_dest_name: "",
        fallback_url: "",
        search_url: "",
        use_ai_filter: false
    });

    // Selectors for Redux State
    // We return these so the component doesn't break immediately, 
    // but typically the component might want to select specific slices (train/flight).
    // For now, we return 'receivedRoutes' as 'allRoutes' to maintain contract.
    const receivedRoutes = useSelector(selectAllRoutes);
    const loading = useSelector(selectTravelLoading);
    const headerLoading = useSelector(selectHeaderLoading);
    const error = useSelector(selectTravelError);

    const socketRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const dataTimeoutRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);
    const globalTimeoutRef = useRef(null);
    const hasReceivedDataRef = useRef(false);
    const isIntentionalClosureRef = useRef(false);
    const pendingMessagesQueueRef = useRef([]); // Robust queue for split-mode messages during reconnection
    const lastPayloadRef = useRef(null); // Track the last payload sent
    const isCompletedRef = useRef(false); // Track if search reached status 201
    const routesBufferRef = useRef([]); // Buffer for batching route updates
    const flushIntervalRef = useRef(null); // Timer for flushing the buffer

    const completionsPendingRef = useRef(0); // Track how many requests we're waiting for before stopping loading

    const stopLoading = useCallback(() => {
        dispatch(setHeaderLoading(false));
        dispatch(setReduxLoading(false));
        completionsPendingRef.current = 0; // Force reset counter on stop
        if (dataTimeoutRef.current) clearTimeout(dataTimeoutRef.current);
        if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
        if (globalTimeoutRef.current) clearTimeout(globalTimeoutRef.current);
        if (flushIntervalRef.current) clearInterval(flushIntervalRef.current);
    }, [dispatch]);

    const flushBuffer = useCallback(() => {
        if (routesBufferRef.current.length > 0) {
            dispatch(addRoutes([...routesBufferRef.current]));
            routesBufferRef.current = [];
        }
    }, [dispatch]);

    const startFlushTimer = useCallback(() => {
        if (flushIntervalRef.current) clearInterval(flushIntervalRef.current);
        flushIntervalRef.current = setInterval(flushBuffer, 500); // Batch updates every 500ms
    }, [flushBuffer]);

    const startDataTimeout = useCallback(() => {
        if (dataTimeoutRef.current) clearTimeout(dataTimeoutRef.current);

        dataTimeoutRef.current = setTimeout(() => {
            if (socketRef.current) {
                isIntentionalClosureRef.current = true;
                socketRef.current.close();
            }
            dispatch(setReduxError('timeout'));
            stopLoading();
        }, 60000);
    }, [dispatch, stopLoading]);

    const startInactivityTimeout = useCallback(() => {
        if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);

        inactivityTimeoutRef.current = setTimeout(() => {
            if (socketRef.current) {
                isIntentionalClosureRef.current = true;
                socketRef.current.close();
            }
            stopLoading();
        }, 15000);
    }, [stopLoading]);

    const startGlobalTimeout = useCallback(() => {
        if (globalTimeoutRef.current) clearTimeout(globalTimeoutRef.current);

        globalTimeoutRef.current = setTimeout(() => {
            if (socketRef.current) {
                // console.log('Global 90s timeout reached, closing connection.');
                isIntentionalClosureRef.current = true;
                socketRef.current.close();
            }
            stopLoading();
        }, 90000); // 90 seconds hard stop
    }, [stopLoading]);

    const connect = useCallback(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN || socketRef.current?.readyState === WebSocket.CONNECTING) {
            return;
        }

        setConnectionStatus('connecting');

        try {
            const socket = new WebSocket(WEBSOCKET_URL);
            socketRef.current = socket;

            socket.onopen = () => {
                if (socket !== socketRef.current) return;
                setConnectionStatus('open');
                reconnectAttemptsRef.current = 0;

                // Flush pending messages queue (Robust handling for split-mode searches)
                if (pendingMessagesQueueRef.current.length > 0) {
                    // console.log(`[WebSocket] Flushing ${pendingMessagesQueueRef.current.length} queued messages`);
                    pendingMessagesQueueRef.current.forEach(msg => {
                        socket.send(JSON.stringify(msg));
                    });
                    pendingMessagesQueueRef.current = []; // Clear queue after flush
                }
            };

            socket.onmessage = (event) => {
                if (socket !== socketRef.current) return;
                try {
                    const data = JSON.parse(event.data);

                    if (data?.data?.routes && data.data.routes.length > 0) {
                        if (!hasReceivedDataRef.current) {
                            if (dataTimeoutRef.current) clearTimeout(dataTimeoutRef.current);
                            hasReceivedDataRef.current = true;
                        }
                        // Batch routes instead of direct dispatch to prevent main thread blocking
                        routesBufferRef.current.push(...data.data.routes);

                        // Extract Search Metadata (Coords, Names, AI Flag, Fallback URL)
                        if (data.data.user_src_ll || data.data.user_dest_ll || data.data.use_ai_filter !== undefined || data.data.fallback_url || data.data.search_url || data.data.routes?.[0]?.search_url) {
                            setSearchMetadata(prev => ({
                                ...prev,
                                user_src_ll: data.data.user_src_ll || prev.user_src_ll,
                                user_dest_ll: data.data.user_dest_ll || prev.user_dest_ll,
                                user_src_name: data.data.user_src_name || prev.user_src_name,
                                user_dest_name: data.data.user_dest_name || prev.user_dest_name,
                                fallback_url: data.data.fallback_url || prev.fallback_url,
                                search_url: data.data.search_url || data.data.routes?.[0]?.search_url || prev.search_url,
                                use_ai_filter: data.data.use_ai_filter !== undefined ? data.data.use_ai_filter : prev.use_ai_filter
                            }));
                        }
                    }

                    if (data?.message === "OK" || data?.message === "ok") {
                        // Intermediate OK/Progress - skip decrementing total count
                        startInactivityTimeout();
                    }

                    // Terminal states for a single request
                    const isTerminal = (data?.status === 201 && data?.message === "Completed") ||
                        (data?.status === 203 || (data?.message && data.message.includes("No routes found"))) ||
                        (data?.status === 500 || (data?.message && data.message.includes("flight_model")));

                    if (isTerminal) {
                        completionsPendingRef.current = Math.max(0, completionsPendingRef.current - 1);

                        // Handle specific terminal behaviors
                        if (data?.status === 201) {
                            isCompletedRef.current = true;
                        } else if (data?.status === 500 || (data?.message && data.message.includes("flight_model"))) {
                            // Backend error is a hard stop for that request
                            dispatch(setReduxError(data.message || "Internal Server Error"));
                        }

                        // Stop global loading only if ALL expected responses have arrived
                        if (completionsPendingRef.current <= 0) {
                            flushBuffer(); // Final flush to ensure no data is left in buffer
                            if (dataTimeoutRef.current) clearTimeout(dataTimeoutRef.current);
                            if (flushIntervalRef.current) clearInterval(flushIntervalRef.current);
                            dispatch(setHeaderLoading(false));
                            dispatch(setReduxLoading(false));
                        }
                        return;
                    }

                    setMessages((prev) => [...prev, data]);
                } catch (err) {
                    console.error('Error parsing WebSocket message:', err);
                }
            };

            socket.onerror = (err) => {
                if (socket !== socketRef.current) return;
                console.error('WebSocket Error:', err);

                // Only show error if we've exhausted reconnect attempts
                if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
                    dispatch(setReduxError('WebSocket connection error'));
                    setConnectionStatus('error');
                }
            };

            socket.onclose = () => {
                if (socket !== socketRef.current) return;
                setConnectionStatus('closed');
                socketRef.current = null;

                if (isIntentionalClosureRef.current) {
                    isIntentionalClosureRef.current = false;
                    dispatch(setReduxLoading(false));
                    dispatch(setHeaderLoading(false));
                    return;
                }

                // If search wasn't completed, prepare to resend the last message upon reconnection
                if (!isCompletedRef.current && lastPayloadRef.current) {
                    // Avoid duplicate payloads in queue
                    const isAlreadyQueued = pendingMessagesQueueRef.current.some(p =>
                        JSON.stringify(p) === JSON.stringify(lastPayloadRef.current)
                    );
                    if (!isAlreadyQueued) {
                        pendingMessagesQueueRef.current.push(lastPayloadRef.current);
                    }
                }

                // Auto Reconnect Logic
                if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                    reconnectTimerRef.current = setTimeout(() => {
                        reconnectAttemptsRef.current += 1;
                        connect();
                    }, RECONNECT_INTERVAL);
                } else {
                    dispatch(setReduxLoading(false));
                    dispatch(setHeaderLoading(false));
                }
            };

        } catch (err) {
            console.error('WebSocket connection initialization error:', err);
            dispatch(setReduxError('Failed to initialize WebSocket'));
            setConnectionStatus('error');
        }
    }, [dispatch, startInactivityTimeout, stopLoading]);

    useEffect(() => {
        connect();

        // Listen for visibility change to reconnect if user comes back to the tab
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                if (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED) {
                    // console.log('App returned to foreground, reconnecting...');
                    connect();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
            }
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }
            if (globalTimeoutRef.current) {
                clearTimeout(globalTimeoutRef.current);
            }
            if (flushIntervalRef.current) {
                clearInterval(flushIntervalRef.current);
            }
        };
    }, [connect]);

    const sendMessage = useCallback((payload, options = {}) => {
        const { skipClear = false } = options;

        // Store the payload for potential resending and reset completion flag
        lastPayloadRef.current = payload;
        isCompletedRef.current = false;

        // Reset/Increment pending count based on whether we are clearing old results
        if (!skipClear) {
            // console.log("-----------------------------------------");
            // console.log("[useTravelWebSocket] INFO: Starting fresh search with Msg 1");
            // console.log("[useTravelWebSocket] Payload:", payload);
            completionsPendingRef.current = 1; // Fresh search expects 1 terminal message
        } else {
            // console.log("[useTravelWebSocket] INFO: Appending sequential Msg 2 (Split Case)");
            // console.log("[useTravelWebSocket] Payload:", payload);
            completionsPendingRef.current += 1; // Appended search adds another expectation
        }

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            dispatch(setReduxLoading(true));
            dispatch(setHeaderLoading(true));
            // Clear previous results on new search unless requested to skip
            if (!skipClear) {
                dispatch(clearRoutes());
            }
            dispatch(setReduxError(null));

            hasReceivedDataRef.current = false;
            isIntentionalClosureRef.current = false;
            startDataTimeout(); // Start the 60s initial timeout
            startGlobalTimeout(); // Start the 90s hard stop
            startFlushTimer(); // Start batching timer

            socketRef.current.send(JSON.stringify(payload));
        } else {
            // 1. Add payload to queue to be sent once connection opens
            pendingMessagesQueueRef.current.push(payload);

            // 2. Clear old data and START loading state IMMEDIATELY (skeletons show)
            if (!skipClear) {
                dispatch(clearRoutes());
            }
            dispatch(setReduxLoading(true));
            dispatch(setHeaderLoading(true));
            dispatch(setReduxError(null));

            // Reset state flags
            hasReceivedDataRef.current = false;
            isIntentionalClosureRef.current = false;
            startDataTimeout();
            startGlobalTimeout();

            // 3. Trigger manual reconnection
            connect();
        }
    }, [connect, dispatch, startDataTimeout]);

    const closeConnection = useCallback(() => {
        if (socketRef.current) {
            isIntentionalClosureRef.current = true;
            socketRef.current.close();
        }
        stopLoading();
    }, [stopLoading]);

    return {
        sendMessage,
        closeConnection,
        connectionStatus,
        receivedRoutes,
        searchMetadata,
        loading,
        headerLoading,
        error
    };
};
